import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import { Rnd } from 'react-rnd';
import api from '../api';
import { toast } from 'react-toastify';
import { FaPenNib, FaCheck, FaChevronLeft, FaChevronRight, FaExpand, FaCompress, FaInfoCircle, FaCopy, FaLayerGroup, FaTrash } from 'react-icons/fa';

// Set worker for react-pdf
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const DocumentSigner = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [documentData, setDocumentData] = useState(null);
    const [savedSignatures, setSavedSignatures] = useState([]);
    const [selectedSavedSignature, setSelectedSavedSignature] = useState(null); // ID of template signature

    // Placed Signatures State
    const [placedSignatures, setPlacedSignatures] = useState([]);
    const [selectedPlacedSignatureId, setSelectedPlacedSignatureId] = useState(null);

    const [numPages, setNumPages] = useState(null);
    const [pageNumber, setPageNumber] = useState(1);
    const [pdfPageSize, setPdfPageSize] = useState(null);
    const [scale, setScale] = useState(1.0);

    // State for placement position
    const [position, setPosition] = useState({ x: 50, y: 100 });
    const [size, setSize] = useState({ width: 150, height: 60 });

    useEffect(() => {
        const fetchData = async () => {
            try {
                // Fetch document details
                const docRes = await api.get('/documents');
                const doc = docRes.data.find(d => d.id === parseInt(id));
                if (doc) {
                    setDocumentData(doc);
                } else {
                    toast.error("Document not found.");
                    navigate('/dashboard');
                    return;
                }

                // Fetch user's saved signature templates
                const sigRes = await api.get('/signatures');
                setSavedSignatures(sigRes.data);

                // Fetch already placed signatures on this document
                await fetchPlacedSignatures();

            } catch (error) {
                console.error(error);
                toast.error("Failed to load data.");
            }
        };
        fetchData();
    }, [id, navigate]);

    const fetchPlacedSignatures = async () => {
        try {
            const res = await api.get(`/documents/${id}/signatures`);
            setPlacedSignatures(res.data);
        } catch (error) {
            console.error("Failed to fetch placed signatures:", error);
        }
    };

    const onDocumentLoadSuccess = ({ numPages }) => setNumPages(numPages);
    const onPageLoadSuccess = (page) => setPdfPageSize({ width: page.width, height: page.height });

    const handleAddSignature = async () => {
        if (!selectedSavedSignature) {
            toast.error("Please select a signature template first.");
            return;
        }
        if (!pdfPageSize) {
            toast.error("PDF page not fully loaded.");
            return;
        }

        const template = savedSignatures.find(s => s.id === selectedSavedSignature);
        if (!template) return;

        // Calculate relative coordinates
        const relX = position.x / (pdfPageSize.width * scale);
        const relY = position.y / (pdfPageSize.height * scale);
        const relW = size.width / (pdfPageSize.width * scale);
        const relH = size.height / (pdfPageSize.height * scale);

        try {
            await api.post(`/documents/${id}/add-signature`, {
                signature_data: template.signature_data,
                page: pageNumber,
                x: relX,
                y: relY,
                w: relW,
                h: relH
            });
            toast.success("Signature added!");
            await fetchPlacedSignatures();

            // Reset position
            setPosition({ x: 50, y: 100 });
        } catch (error) {
            console.error(error);
            toast.error("Failed to add signature.");
        }
    };

    const handleDeletePlacedSignature = async (signatureId) => {
        try {
            await api.delete(`/documents/${id}/signatures/${signatureId}`);
            toast.success("Signature removed!");
            await fetchPlacedSignatures();
        } catch (error) {
            console.error(error);
            toast.error("Failed to delete signature.");
        }
    };

    const handleDuplicateSignature = async (signatureId) => {
        try {
            await api.post(`/documents/${id}/signatures/${signatureId}/duplicate`);
            toast.success("Signature duplicated!");
            await fetchPlacedSignatures();
        } catch (error) {
            console.error(error);
            toast.error("Failed to duplicate signature.");
        }
    };

    const handleApplyToAllPages = async (signatureId) => {
        if (!numPages) return;
        try {
            await api.post(`/documents/${id}/signatures/${signatureId}/apply-all`, {
                num_pages: numPages
            });
            toast.success(`Signature applied to all ${numPages} pages!`);
            await fetchPlacedSignatures();
        } catch (error) {
            console.error(error);
            toast.error("Failed to apply to all pages.");
        }
    };

    const handleFinalize = async () => {
        if (placedSignatures.length === 0) {
            toast.error("Please add at least one signature before finalizing.");
            return;
        }

        try {
            const res = await api.post(`/documents/${id}/finalize`);
            toast.success("Document finalized successfully!");

            // Start download logic
            const doc = res.data.document;
            const downloadUrl = `${api.defaults.baseURL || '/api'}/documents/${id}/download`;
            window.open(downloadUrl, '_blank');

            navigate('/dashboard');
        } catch (error) {
            console.error(error);
            toast.error("Failed to finalize document.");
        }
    };

    if (!documentData) return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', background: '#f9fafb' }}>
            <div style={{ fontSize: '1.5rem', color: '#4a00e0', fontWeight: 600 }}>Loading Document...</div>
        </div>
    );

    const fileUrl = `/storage/${documentData.original_file_path}`;
    const currentPageSignatures = placedSignatures.filter(sig => sig.page === pageNumber);
    const selectedTemplateData = savedSignatures.find(s => s.id === selectedSavedSignature);

    return (
        <div style={{ display: 'flex', height: 'calc(100vh - 80px)', background: '#f3f4f6', overflow: 'hidden' }}>
            {/* Main PDF Viewer Area */}
            <div style={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
                {/* Toolbar */}
                <div style={{
                    padding: '1rem',
                    background: 'white',
                    borderBottom: '1px solid #e5e7eb',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.02)',
                    zIndex: 10
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#1f2937', margin: 0 }}>{documentData.title || 'Untitled Document'}</h2>
                        <span style={{ fontSize: '0.85rem', padding: '0.2rem 0.6rem', background: '#e0e7ff', color: '#4338ca', borderRadius: '12px' }}>
                            Page {pageNumber} of {numPages}
                        </span>
                        <span style={{ fontSize: '0.85rem', padding: '0.2rem 0.6rem', background: '#dcfce7', color: '#166534', borderRadius: '12px' }}>
                            {placedSignatures.length} signature{placedSignatures.length !== 1 ? 's' : ''}
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <button onClick={() => setScale(s => Math.max(0.5, s - 0.1))} style={toolbarBtnStyle}><FaCompress /></button>
                        <span style={{ display: 'flex', alignItems: 'center', fontSize: '0.9rem', width: '50px', justifyContent: 'center' }}>{Math.round(scale * 100)}%</span>
                        <button onClick={() => setScale(s => Math.min(2.0, s + 0.1))} style={toolbarBtnStyle}><FaExpand /></button>
                    </div>
                </div>

                {/* PDF Container */}
                <div style={{ flex: 1, overflow: 'auto', padding: '2rem', display: 'flex', justifyContent: 'center', background: '#e5e7eb' }}>
                    <div style={{
                        position: 'relative',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                        transition: 'transform 0.2s ease',
                        width: pdfPageSize ? pdfPageSize.width * scale : 'auto',
                        height: pdfPageSize ? pdfPageSize.height * scale : 'auto'
                    }}>
                        <Document
                            file={fileUrl}
                            onLoadSuccess={onDocumentLoadSuccess}
                            onLoadError={(error) => console.error('Error while loading document!', error.message)}
                        >
                            <Page
                                pageNumber={pageNumber}
                                scale={scale}
                                renderTextLayer={false}
                                renderAnnotationLayer={false}
                                onLoadSuccess={onPageLoadSuccess}
                            />
                        </Document>

                        {/* Ghost/Preview Signature removed as per user request */}
                        {/* Only Placed Signatures are shown below */}

                        {/* Placed Signatures on Current Page */}
                        {/* Placed Signatures on Current Page */}
                        {currentPageSignatures.map(sig => (
                            <Rnd
                                key={sig.id}
                                size={{
                                    width: sig.w * pdfPageSize.width * scale,
                                    height: sig.h * pdfPageSize.height * scale
                                }}
                                position={{
                                    x: sig.x * pdfPageSize.width * scale,
                                    y: sig.y * pdfPageSize.height * scale
                                }}
                                disableDragging={selectedPlacedSignatureId !== sig.id}
                                enableResizing={selectedPlacedSignatureId === sig.id ? {
                                    bottomRight: true, bottomRight: true, topRight: true, bottomLeft: true, topLeft: true
                                } : false}
                                onDragStop={async (e, d) => {
                                    const newX = d.x / (pdfPageSize.width * scale);
                                    const newY = d.y / (pdfPageSize.height * scale);

                                    // Optimistic update
                                    setPlacedSignatures(prev => prev.map(s => s.id === sig.id ? { ...s, x: newX, y: newY } : s));

                                    try {
                                        await api.put(`/documents/${id}/signatures/${sig.id}`, {
                                            x: newX,
                                            y: newY,
                                            w: sig.w,
                                            h: sig.h
                                        });
                                    } catch (error) {
                                        console.error("Failed to update position", error);
                                        toast.error("Failed to save new position");
                                        await fetchPlacedSignatures(); // Revert on error
                                    }
                                }}
                                onResizeStop={async (e, direction, ref, delta, position) => {
                                    const newW = parseInt(ref.style.width) / (pdfPageSize.width * scale);
                                    const newH = parseInt(ref.style.height) / (pdfPageSize.height * scale);
                                    const newX = position.x / (pdfPageSize.width * scale);
                                    const newY = position.y / (pdfPageSize.height * scale);

                                    // Optimistic update
                                    setPlacedSignatures(prev => prev.map(s => s.id === sig.id ? { ...s, x: newX, y: newY, w: newW, h: newH } : s));

                                    try {
                                        await api.put(`/documents/${id}/signatures/${sig.id}`, {
                                            x: newX,
                                            y: newY,
                                            w: newW,
                                            h: newH
                                        });
                                    } catch (error) {
                                        console.error("Failed to update size", error);
                                        toast.error("Failed to save new size");
                                        await fetchPlacedSignatures();
                                    }
                                }}
                                bounds="parent"
                                style={{
                                    border: selectedPlacedSignatureId === sig.id ? '2px dashed #4a00e0' : '2px solid transparent',
                                    borderRadius: '4px',
                                    cursor: selectedPlacedSignatureId === sig.id ? 'move' : 'pointer',
                                    zIndex: selectedPlacedSignatureId === sig.id ? 50 : 10
                                }}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedPlacedSignatureId(sig.id);
                                }}
                            >
                                <img
                                    src={sig.signature_data}
                                    alt="Placed Sig"
                                    style={{ width: '100%', height: '100%', objectFit: 'contain', pointerEvents: 'none' }}
                                />
                                {selectedPlacedSignatureId === sig.id && (
                                    <>
                                        <div
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setSelectedPlacedSignatureId(null);
                                            }}
                                            style={{
                                                position: 'absolute',
                                                top: '-30px',
                                                right: '0',
                                                background: '#10b981',
                                                color: 'white',
                                                borderRadius: '4px',
                                                padding: '2px 8px',
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                boxShadow: '0 2px 5px rgba(0,0,0,0.2)',
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '4px'
                                            }}
                                        >
                                            <FaCheck size={10} /> Done
                                        </div>
                                        <div style={{ position: 'absolute', top: '-10px', left: '-10px', background: '#dc2626', color: 'white', borderRadius: '50%', padding: '4px', boxShadow: '0 2px 5px rgba(0,0,0,0.2)', cursor: 'pointer' }}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleDeletePlacedSignature(sig.id);
                                            }}
                                        >
                                            <FaTrash size={10} />
                                        </div>
                                    </>
                                )}
                            </Rnd>
                        ))}
                    </div>
                </div>

                {/* Pagination Controls */}
                {numPages && (
                    <div style={{
                        position: 'absolute',
                        bottom: '2rem',
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(8px)',
                        padding: '0.5rem 1rem',
                        borderRadius: '50px',
                        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                        display: 'flex',
                        gap: '1rem',
                        alignItems: 'center',
                        zIndex: 20
                    }}>
                        <button disabled={pageNumber <= 1} onClick={() => setPageNumber(prev => prev - 1)} style={navBtnStyle}>
                            <FaChevronLeft />
                        </button>
                        <span style={{ fontWeight: 600, color: '#374151' }}>{pageNumber} / {numPages}</span>
                        <button disabled={pageNumber >= numPages} onClick={() => setPageNumber(prev => prev + 1)} style={navBtnStyle}>
                            <FaChevronRight />
                        </button>
                    </div>
                )}
            </div>

            {/* Right Sidebar */}
            <div style={{
                width: '380px',
                background: 'white',
                borderLeft: '1px solid #e5e7eb',
                display: 'flex',
                flexDirection: 'column',
                boxShadow: '-4px 0 20px rgba(0,0,0,0.02)',
                zIndex: 30,
                overflowY: 'auto'
            }}>
                <div style={{ padding: '2rem', borderBottom: '1px solid #f3f4f6' }}>
                    <h3 style={{ fontSize: '1.5rem', fontWeight: 800, color: '#111827', marginBottom: '0.5rem' }}>Sign Document</h3>
                    <p style={{ color: '#6b7280', fontSize: '0.9rem' }}>Select a signature template, then perform actions.</p>
                </div>

                <div style={{ flex: 1, padding: '2rem' }}>
                    <div style={{ marginBottom: '2rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                            <label style={{ fontWeight: 600, color: '#374151' }}>Saved Signatures</label>
                            <button onClick={() => navigate('/create-signature')} style={{ fontSize: '0.85rem', color: '#4a00e0', fontWeight: 600, background: 'none', border: 'none', cursor: 'pointer' }}>+ Create New</button>
                        </div>

                        {/* Signature Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', maxHeight: '200px', overflowY: 'auto', marginBottom: '1rem' }}>
                            {savedSignatures.length > 0 ? savedSignatures.map(sig => (
                                <div
                                    key={sig.id}
                                    onClick={() => setSelectedSavedSignature(sig.id)}
                                    style={{
                                        border: selectedSavedSignature === sig.id ? '2px solid #4a00e0' : '1px solid #e5e7eb',
                                        borderRadius: '12px',
                                        padding: '1rem',
                                        cursor: 'pointer',
                                        background: selectedSavedSignature === sig.id ? 'rgba(74, 0, 224, 0.03)' : 'white',
                                        transition: 'all 0.2s ease',
                                        position: 'relative',
                                        height: '80px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <img src={sig.signature_data} alt="Sig" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                    {selectedSavedSignature === sig.id && (
                                        <div style={{ position: 'absolute', top: '4px', right: '4px', color: '#4a00e0' }}>
                                            <FaCheckCircle size={14} />
                                        </div>
                                    )}
                                </div>
                            )) : (
                                <div style={{ gridColumn: '1 / -1', textAlign: 'center', padding: '1rem', background: '#f9fafb', borderRadius: '12px', color: '#6b7280' }}>
                                    <p>No saved signatures.</p>
                                </div>
                            )}
                        </div>

                        {/* Add Button */}
                        <button
                            onClick={handleAddSignature}
                            disabled={!selectedSavedSignature || documentData.status === 'signed'}
                            className="btn-primary"
                            style={{
                                width: '100%',
                                padding: '0.8rem',
                                borderRadius: '10px',
                                background: !selectedSavedSignature || documentData.status === 'signed' ? '#e5e7eb' : '#4a00e0',
                                color: !selectedSavedSignature || documentData.status === 'signed' ? '#9ca3af' : 'white',
                                cursor: !selectedSavedSignature || documentData.status === 'signed' ? 'not-allowed' : 'pointer'
                            }}
                        >
                            + Add Selected to Page {pageNumber}
                        </button>
                    </div>

                    {/* Placed Signatures List */}
                    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                        <h4 style={{ fontSize: '1rem', fontWeight: 700, color: '#374151', marginBottom: '1rem', flexShrink: 0 }}>Placed Signatures ({placedSignatures.length})</h4>
                        {placedSignatures.length === 0 ? (
                            <div style={{ padding: '1.5rem', background: '#f9fafb', borderRadius: '12px', textAlign: 'center', color: '#6b7280' }}>
                                <p style={{ fontSize: '0.9rem' }}>No signatures placed yet.</p>
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', overflowY: 'auto' }}>
                                {/* Group signatures by page */}
                                {Object.entries(placedSignatures.reduce((acc, sig) => {
                                    (acc[sig.page] = acc[sig.page] || []).push(sig);
                                    return acc;
                                }, {})).sort((a, b) => Number(a[0]) - Number(b[0])).map(([page, signatures]) => (
                                    <div key={page} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '0.25rem 0.5rem',
                                            background: '#f3f4f6',
                                            borderRadius: '6px',
                                            color: '#4b5563',
                                            fontSize: '0.8rem',
                                            fontWeight: 700
                                        }}>
                                            <span>Page {page}</span>
                                            <span style={{
                                                background: '#e5e7eb',
                                                padding: '0 6px',
                                                borderRadius: '10px',
                                                fontSize: '0.75rem'
                                            }}>{signatures.length}</span>
                                        </div>

                                        {signatures.map(sig => (
                                            <div
                                                key={sig.id}
                                                style={{
                                                    border: selectedPlacedSignatureId === sig.id ? '2px solid #10b981' : '1px solid #e5e7eb',
                                                    borderRadius: '12px',
                                                    padding: '0.75rem',
                                                    background: selectedPlacedSignatureId === sig.id ? 'rgba(16, 185, 129, 0.03)' : 'white',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.2s',
                                                    position: 'relative'
                                                }}
                                                onClick={() => {
                                                    setSelectedPlacedSignatureId(sig.id);
                                                    setPageNumber(sig.page);
                                                }}
                                            >
                                                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: '0.5rem' }}>
                                                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                        <button onClick={(e) => { e.stopPropagation(); handleDuplicateSignature(sig.id); }} style={{ padding: '0.25rem 0.5rem', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#4b5563' }} title="Duplicate">
                                                            <FaCopy size={12} />
                                                        </button>
                                                        {/* Apply to all logic: Only show if numPages > 1 */}
                                                        {numPages > 1 && (
                                                            <button onClick={(e) => { e.stopPropagation(); handleApplyToAllPages(sig.id); }} style={{ padding: '0.25rem 0.5rem', background: '#f3f4f6', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#4b5563' }} title="Apply to all pages">
                                                                <FaLayerGroup size={12} />
                                                            </button>
                                                        )}
                                                        <button onClick={(e) => { e.stopPropagation(); handleDeletePlacedSignature(sig.id); }} style={{ padding: '0.25rem 0.5rem', background: '#fee2e2', border: 'none', borderRadius: '6px', cursor: 'pointer', color: '#dc2626' }} title="Delete">
                                                            <FaTrash size={12} />
                                                        </button>
                                                    </div>
                                                </div>
                                                <div style={{ height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', borderRadius: '8px', padding: '0.25rem' }}>
                                                    <img src={sig.signature_data} alt="Sig" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
                                                </div>
                                                {selectedPlacedSignatureId === sig.id && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        left: '-4px',
                                                        top: '50%',
                                                        transform: 'translateY(-50%)',
                                                        width: '4px',
                                                        height: '60%',
                                                        background: '#10b981',
                                                        borderRadius: '0 4px 4px 0'
                                                    }} />
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div style={{ padding: '2rem', borderTop: '1px solid #f3f4f6', background: 'white' }}>
                    <button
                        onClick={handleFinalize}
                        disabled={placedSignatures.length === 0}
                        style={{
                            width: '100%',
                            padding: '1rem',
                            background: placedSignatures.length > 0 ? 'linear-gradient(135deg, #4a00e0 0%, #8e2de2 100%)' : '#e5e7eb',
                            color: placedSignatures.length > 0 ? 'white' : '#9ca3af',
                            border: 'none',
                            fontSize: '1.1rem',
                            fontWeight: 700,
                            cursor: placedSignatures.length > 0 ? 'pointer' : 'not-allowed',
                            borderRadius: '12px',
                            boxShadow: placedSignatures.length > 0 ? '0 10px 20px -5px rgba(74, 0, 224, 0.4)' : 'none',
                            transition: 'all 0.3s ease',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '0.5rem'
                        }}
                    >
                        <FaPenNib /> Finalize & Download
                    </button>
                </div>
            </div>
        </div>
    );
};

const toolbarBtnStyle = {
    background: 'white',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '0.5rem',
    cursor: 'pointer',
    color: '#4b5563',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s'
};

const navBtnStyle = {
    background: 'white',
    border: 'none',
    borderRadius: '50%',
    width: '36px',
    height: '36px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    color: '#4b5563',
    boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
};

const FaCheckCircle = ({ size }) => (
    <svg stroke="currentColor" fill="currentColor" strokeWidth="0" viewBox="0 0 512 512" height={size} width={size} xmlns="http://www.w3.org/2000/svg">
        <path d="M504 256c0 136.967-111.033 248-248 248S8 392.967 8 256 119.033 8 256 8s248 111.033 248 248zM227.314 387.314l184-184c6.248-6.248 6.248-16.379 0-22.627l-22.627-22.627c-6.248-6.249-16.379-6.249-22.628 0L216 308.118l-70.059-70.059c-6.248-6.248-16.379-6.248-22.628 0l-22.627 22.627c-6.248 6.248-6.248 16.379 0 22.627l104 104c6.249 6.249 16.379 6.249 22.628 0z"></path>
    </svg>
);

export default DocumentSigner;
